/** Per-strategy daily-config CRUD (P83).
 *
 * Routes (backend, mounted under app/api/daily_config.py):
 *   GET  /api/strategies/{strategy_id}/daily-config?trade_date=YYYY-MM-DD
 *   PUT  /api/strategies/{strategy_id}/daily-config
 */

import { getJson } from "@/lib/http";

/** BOTH (2026-08-01) is the morning seed and means "no side chosen": an
 * AUTO engine derives its own direction, so it trades either side; CALL or
 * PUT narrow it to one; NO_TRADE halts it. Manual paths reject BOTH. */
export type Bias = "CALL" | "PUT" | "NO_TRADE" | "BOTH";
export type ZoneMode = "MANUAL" | "SUGGEST" | "AUTO";
export type ExitMode = "FIXED" | "TRAIL";
/** Which option side(s) the supertrend engine may ENTER on (ST-5e). */
export type SupertrendSides = "BOTH" | "CALL" | "PUT";
/** Strike moneyness for supertrend contract selection (ST-6). Default ITM
 * = unchanged. ATM ignores depth; OTM mirrors ITM (depth capped at 2). */
export type StrikeMoneyness = "ITM" | "ATM" | "OTM";
/** Strike depth: steps away from the ATM. ITM allows 1–4, OTM 1–2. */
export type StrikeDepth = 1 | 2 | 3 | 4;

/** Supertrend config fields (ST-5). Ignored by bias strategies; null/
 * undefined means "engine default" (atr 10 / factor 3.0 / lower TF 1m). */
export interface SupertrendConfigFields {
  exit_mode: ExitMode;
  max_entries_per_day: number | null;
  atr_period: number | null;
  factor: number | null;
  lower_tf: number | null;
  sides: SupertrendSides;
  /** Supertrend armed entries (2026-07-13): null = disabled (engine
   * default flipped OFF 2026-07-15 — plain enter-on-cross). */
  pivot_entry: boolean | null;
  liq_grab_entry: boolean | null;
}

/** BB bottom-buy config fields (BB-6). Ignored by other engines; null
 * means "engine default" (the Pine-script values). `rsi_enabled` null =
 * filter ON. */
export interface BbConfigFields {
  bb_length: number | null;
  bb_mult: number | null;
  rsi_enabled: boolean | null;
  rsi_length: number | null;
  rsi_oversold: number | null;
  green_body_pct: number | null;
  doji_body_pct: number | null;
  trigger_valid_bars: number | null;
  /** NULL -> ON: strong-green signal candles enter at their own close. */
  instant_entry: boolean | null;
}

export interface StrategyDailyConfig extends SupertrendConfigFields, BbConfigFields {
  strategy_id: string;
  trade_date: string; // YYYY-MM-DD
  bias: Bias;
  lots: number;
  sl_offset_points: number;
  trail_trigger_points: number;
  trail_amount_points: number;
  /** Rung-1 cost buffer (2026-07-15 ladder rework). 0 = breakeven. */
  trail_slippage_points: number;
  itm_offset: StrikeDepth;
  strike_moneyness: StrikeMoneyness;
  default_zone_mode: ZoneMode;
  /** Shadow-exit simulation inputs (P50). target null = instrument default. */
  /** The swing the position books at (default 10) and the entry
   * sweep's own dimension (default 5). Shadow keeps its own
   * constants and is no longer operator-configurable. */
  target_pivot: number;
  sweep_pivot: number;
  created_at: string;
  updated_at: string;
}

export interface StrategyDailyConfigInput {
  trade_date?: string;
  bias: Bias;
  lots: number;
  sl_offset_points: number;
  trail_trigger_points: number;
  trail_amount_points: number;
  /** Omitted → backend applies the day's per-underlying default. */
  trail_slippage_points?: number;
  itm_offset: StrikeDepth;
  strike_moneyness?: StrikeMoneyness;
  default_zone_mode?: ZoneMode;
  target_pivot?: number;
  sweep_pivot?: number;
  exit_mode?: ExitMode;
  max_entries_per_day?: number | null;
  atr_period?: number | null;
  factor?: number | null;
  lower_tf?: number | null;
  sides?: SupertrendSides;
  pivot_entry?: boolean | null;
  liq_grab_entry?: boolean | null;
  /** BB bottom-buy config (BB-6). Omitted → engine default; rsi_enabled
   * omitted → filter ON. */
  bb_length?: number | null;
  bb_mult?: number | null;
  rsi_enabled?: boolean | null;
  rsi_length?: number | null;
  rsi_oversold?: number | null;
  green_body_pct?: number | null;
  doji_body_pct?: number | null;
  trigger_valid_bars?: number | null;
  instant_entry?: boolean | null;
}

function url(strategyId: string): string {
  return `/api/strategies/${strategyId}/daily-config`;
}

export const strategyDailyConfigApi = {
  get(strategyId: string, tradeDate?: string): Promise<StrategyDailyConfig> {
    const q = tradeDate ? `?trade_date=${encodeURIComponent(tradeDate)}` : "";
    return getJson<StrategyDailyConfig>(`${url(strategyId)}${q}`);
  },
  upsert(strategyId: string, payload: StrategyDailyConfigInput): Promise<StrategyDailyConfig> {
    return getJson<StrategyDailyConfig>(url(strategyId), {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  },
};
